import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Search,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Zap,
  Code2,
  MapPin,
  ShoppingBag,
  Bot,
  Link2,
} from "lucide-react";

export const Route = createFileRoute("/services_/seo")({
  head: () => ({
    meta: [
      {
        title:
          "SEO Services — KMWAdvertising · Search Engine Optimization & GEO",
      },
      {
        name: "description",
        content:
          "SEO Services That Improve Search Rankings, Increase Organic Traffic and Drive Sustainable Business Growth. Technical SEO, Link Building & AI Search (GEO) by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "SEO Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Grow Your Business with Data-Driven Search Engine Optimization. Organic traffic, technical site speed, and AI search readiness across Google, ChatGPT & Perplexity.",
      },
    ],
  }),
  component: SEOServicePage,
});

export function SEOServicePage() {
  const whySEOMatters = [
    {
      title: "Organic Visibility",
      desc: "Appear at the top of search engine results when high-intent customers search for your products without relying solely on ad spend.",
      icon: Search,
    },
    {
      title: "High-Intent Traffic",
      desc: "Attract users actively seeking solutions, resulting in higher lead quality, stronger engagement, and better conversion rates.",
      icon: Target,
    },
    {
      title: "Brand Authority & Trust",
      desc: "Top organic rankings reinforce brand credibility and position your business as an authoritative industry leader.",
      icon: Award,
    },
    {
      title: "Superior User Experience",
      desc: "Optimizing website speed, mobile responsiveness, Core Web Vitals, and navigation improves satisfaction for users and search bots.",
      icon: Zap,
    },
    {
      title: "Compounding Long-Term ROI",
      desc: "Unlike paid campaigns that stop when ad budgets end, SEO continues generating organic traffic and leads over time.",
      icon: TrendingUp,
    },
  ];

  const technicalSEOTactics = [
    "Comprehensive Website & Code SEO Audit",
    "Crawl error resolution & indexation fixes",
    "Site architecture & internal link hierarchy",
    "XML sitemap creation & submission",
    "Robots.txt & crawl budget optimization",
    "HTTPS security & SSL compliance",
    "Core Web Vitals (LCP, INP, CLS) tuning",
    "Mobile-first responsive optimization",
    "Page speed & image compression",
    "Structured Data & Schema Markup (JSON-LD)",
  ];

  const onPageSEOTactics = [
    "Keyword research & search intent mapping",
    "SEO-friendly URL structure optimization",
    "Title tag & Meta description copywriting",
    "Header tag hierarchy (H1, H2, H3) tuning",
    "Contextual internal linking strategy",
    "Image alt text & file name optimization",
    "High-converting content enhancement",
    "Entity & Semantic SEO optimization",
  ];

  const offPageSEOTactics = [
    "Strategic Digital PR campaigns",
    "High-authority white-hat link building",
    "Niche guest posting & editorial outreach",
    "Local citation & directory management",
    "Unlinked brand mention reclamation",
    "Competitor backlink gap analysis",
    "Online brand reputation monitoring",
    "Ethical search engine guideline compliance",
  ];

  const specializedSEOSuites = [
    {
      title: "Local SEO & Google Business Profile",
      desc: "Google Business Profile optimization, local citation management, location landing pages, map pack ranking, and local review strategies.",
      icon: MapPin,
    },
    {
      title: "E-Commerce SEO",
      desc: "Category page optimization, product schema, faceted navigation indexing, merchant feed management, and internal linking for online stores.",
      icon: ShoppingBag,
    },
    {
      title: "Enterprise SEO at Scale",
      desc: "Multi-location, international SEO, enterprise-scale technical audits, log file analysis, content governance, and executive reporting dashboards.",
      icon: Building2,
    },
    {
      title: "AI Search & GEO (Generative Engine Optimization)",
      desc: "Optimizing content structure for Google AI Overviews, ChatGPT Search, Microsoft Copilot, Perplexity AI, and Gemini LLM answers.",
      icon: Bot,
    },
  ];

  const seoProcessSteps = [
    { num: "01", title: "Website & Competitor Audit", desc: "Deep technical, content, and backlink audit against top search competitors." },
    { num: "02", title: "Keyword & Intent Mapping", desc: "Identifying high-volume commercial keywords aligned with customer buying stages." },
    { num: "03", title: "Technical SEO Fixes", desc: "Resolving crawl blockers, indexing issues, and Core Web Vitals performance bottlenecks." },
    { num: "04", title: "On-Page & Semantic Tuning", desc: "Optimizing titles, meta tags, heading structures, and entity-rich page copy." },
    { num: "05", title: "Content Strategy & Creation", desc: "Developing authoritative, expert content that satisfies search intent and user queries." },
    { num: "06", title: "Authority & Link Building", desc: "Securing high-domain-authority editorial backlinks and digital PR coverage." },
    { num: "07", title: "Performance Tracking & Reporting", desc: "Monitoring organic rankings, organic traffic sessions, conversions, and keyword positions." },
    { num: "08", title: "Continuous Iteration & GEO", desc: "Adapting strategies to search engine algorithm updates and AI Search engines." },
  ];

  const kmwaStrengths = [
    "25+ years of strategic media & digital marketing leadership",
    "Technical SEO & web performance engineering specialists",
    "Content-driven, user-first optimization framework",
    "100% white-hat, ethical search engine compliance",
    "AI Search & Generative Engine (GEO) optimization readiness",
    "Transparent keyword rank & revenue attribution reporting",
    "Cross-channel integration with PR, Content & Digital Media",
    "Enterprise & multi-location scaling capabilities",
    "Conversion-rate-focused organic optimization",
    "Long-term partnership & sustained organic growth",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">SEO Services</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Search className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                SEO Services That Improve Search Rankings, Increase Organic Traffic and Drive Sustainable Business Growth
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Search Engine Optimization & Generative Engine Optimization (GEO)
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Grow Your Business with Data-Driven Search Engine Optimization
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  In today's digital-first world, your customers begin their buying journey with a search. Whether looking for products, services or industry solutions, appearing at the top of search engine results directly drives brand visibility, qualified website traffic and business revenue. Search Engine Optimization (SEO) helps businesses earn that visibility organically.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Unlike paid advertising, SEO delivers compounding long-term value by attracting visitors actively searching for what you offer. A well-executed SEO strategy enhances site speed, Core Web Vitals, domain authority, and user experience across traditional and AI-powered search engines.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide comprehensive SEO services combining technical excellence, strategic content planning, authoritative link building, and AI Search / Generative Engine Optimisation (GEO).
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Free SEO & Audit Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY SEO MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Sustainable Traffic & Authority
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why SEO Matters for Your Business
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                SEO is more than improving rankings. It ensures your business becomes discoverable, credible and competitive in an increasingly crowded digital marketplace.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whySEOMatters.map((item, idx) => {
                const SIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <SIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* TECHNICAL, ON-PAGE & OFF-PAGE SEO */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Full-Funnel Organic Optimization
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Core SEO Capabilities
              </h2>
            </div>

            {/* TECHNICAL & ON-PAGE GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* TECHNICAL */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Code2 className="h-3.5 w-3.5" />
                    Crawlability & Performance
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Technical SEO</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Build a Strong Foundation for Search Engine Indexation
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Ensuring search engine bots and AI crawlers can efficiently index, parse, and understand your website's architecture and Core Web Vitals.
                </p>

                <div className="space-y-2 pt-2">
                  {technicalSEOTactics.map((tech, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{tech}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ON-PAGE & OFF-PAGE */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <Link2 className="h-3.5 w-3.5" />
                    Content Relevance & Authority
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">On-Page & Off-Page SEO</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">On-Page Content Tuning:</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {onPageSEOTactics.slice(0, 4).map((op, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-sky-400 shrink-0" />
                          <span>{op}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-white/10">
                    <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-2">Off-Page & Digital PR Link Building:</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {offPageSEOTactics.slice(0, 4).map((off, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                          <Check className="h-3 w-3 text-sky-400 shrink-0" />
                          <span>{off}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SPECIALIZED SEO SUITES (LOCAL, E-COM, ENTERPRISE, GEO) */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Specialized SEO Solutions & AI Search (GEO)
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Tailored search optimization programs designed for specific market dynamics and AI engine visibility.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {specializedSEOSuites.map((suite, idx) => {
                  const FIcon = suite.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <FIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{suite.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{suite.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* OUR 8-STEP SEO PROCESS */}
            <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/10">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Our Structured 8-Step SEO Process
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  A transparent, data-driven workflow delivering sustainable rankings and traffic.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {seoProcessSteps.map((step, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                    <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">{step.num}</span>
                    <h5 className="text-sm font-bold text-white">{step.title}</h5>
                    <p className="text-xs text-muted-foreground leading-snug">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Search & Media Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Search className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              SEO Growth Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Grow Through Organic Search?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our SEO specialists today for a comprehensive website audit and a customised search engine optimisation strategy tailored to your goals.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Website SEO Audit
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
